class FeatureShowcase extends HTMLElement {
	constructor() {
		super();
		this.currentTabIndex = 0;
		this.tabs = [];
		this.mediaSlides = [];
		this.isTransitioning = false;
		this.isInitialized = false;
		this.resizeObserver = null;
		this.isMobile = false;
	}

	static get observedAttributes() {
		return ['title', 'tabs'];
	}

	attributeChangedCallback(name, oldValue, newValue) {
		if (oldValue !== newValue && this.isInitialized) {
			this.reinitialize();
		}
	}

	connectedCallback() {
		this.initialize();
	}

	disconnectedCallback() {
		this.cleanup();
	}

	async initialize() {
		try {
			this.checkMobile();
			this.parseAttributes();
			this.extractMediaSlides();
			this.render();
			await this.waitForLayout();
			this.bindEvents();
			this.initializeUI();
			this.handleResize();
			this.isInitialized = true;
		} catch (error) {
			console.error('Failed to initialize FeatureShowcase:', error);
		}
	}

	async reinitialize() {
		this.cleanup();
		this.isInitialized = false;
		await this.initialize();
	}

	checkMobile() {
		this.isMobile = window.innerWidth < 1024; // lg断点
	}

	handleResize() {
		window.addEventListener('resize', () => {
			const wasMobile = this.isMobile;
			this.checkMobile();
			if (wasMobile !== this.isMobile) {
				this.reinitialize(); // 重新渲染当布局需要改变时
			}
		});
	}

	parseAttributes() {
		this.title = this.getAttribute('title') || 'Customize Your Characters';

		const tabsAttr = this.getAttribute('tabs');
		if (tabsAttr) {
			try {
				this.tabs = JSON.parse(decodeURIComponent(tabsAttr));
			} catch (e) {
				console.error('Invalid tabs JSON:', e);
				this.setDefaultTabs();
			}
		} else {
			this.setDefaultTabs();
		}
	}

	extractMediaSlides() {
		// 从 liquid 渲染的媒体内容中提取媒体元素
		const mediaContainer = this.querySelector('.media-slides-container');
		if (mediaContainer) {
			this.mediaSlides = Array.from(
				mediaContainer.querySelectorAll('.media-slide')
			);
			// 隐藏原始容器
			mediaContainer.style.display = 'none';
		} else {
			this.mediaSlides = [];
		}
	}

	setDefaultTabs() {
		this.tabs = [
			{
				label: 'Unique in its Look',
				description:
					'Upload your 3D models* with an easy-to-use interface! Import from your favorite platforms such as MMD, VRoid or SketchFab, or choose from our library to create a character that looks exactly how you envision. From anime heroes to original creations, the choice is yours!\n*Supported formats: VRM, FBX, GLTF, etc.'
			},
			{
				label: 'Shape Their Soul',
				description:
					"Define your character's personality, backstory, and unique traits. Create deep, meaningful personalities that will make your characters truly come alive in every interaction."
			}
		];
	}

	render() {
		if (this.isMobile) {
			this.renderMobile();
		} else {
			this.renderDesktop();
		}

		// 渲染完成后插入媒体内容
		this.insertMediaContent();
	}

	insertMediaContent() {
		const mediaContainer = this.querySelector('#media-slider-container');
		if (mediaContainer && this.mediaSlides.length > 0) {
			// 清空容器
			mediaContainer.innerHTML = '';

			// 创建媒体滑块
			const mediaSlider = document.createElement('div');
			mediaSlider.className = 'media-slider';
			mediaSlider.id = 'media-slider';
			mediaSlider.style.cssText = `
				display: flex;
				width: ${this.mediaSlides.length * 100}%;
				height: 100%;
				transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
				transform: translateX(-${
					(this.currentTabIndex * 100) / this.mediaSlides.length
				}%);
			`;

			// 为每个媒体创建滑块项
			this.mediaSlides.forEach((mediaSlide, index) => {
				const slideItem = document.createElement('div');
				slideItem.className = 'media-slide-item';
				slideItem.style.cssText = `
					width: ${100 / this.mediaSlides.length}%;
					height: 100%;
					flex-shrink: 0;
				`;

				// 克隆媒体内容
				const clonedMedia = mediaSlide.cloneNode(true);
				clonedMedia.style.cssText = `
					width: 100%;
					height: 100%;
					display: block;
				`;

				slideItem.appendChild(clonedMedia);
				mediaSlider.appendChild(slideItem);
			});

			mediaContainer.appendChild(mediaSlider);
		}
	}

	renderMobile() {
		this.innerHTML = `
			<style>
				.media-container {
					position: relative;
					overflow: hidden;
					aspect-ratio: 16/9;
					border-radius: 12px;
				}

				.media-slider {
					display: flex;
					width: 100%;
					height: 100%;
					transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
				}

				.media-slide-item {
					flex-shrink: 0;
				}

				.media-slide-item .media-slide {
					width: 100%;
					height: 100%;
				}

				.media-slide-item .feature-video,
				.media-slide-item .feature-image {
					width: 100%;
					height: 100%;
					object-fit: cover;
					border-radius: 12px;
				}

				.mobile-tab-container {
					position: relative;
					overflow: hidden;
					margin: 0 -1rem;
				}

				.mobile-tab-scroll {
					display: flex;
					gap: 0.75rem;
					padding: 0 1rem;
					overflow-x: auto;
					-webkit-overflow-scrolling: touch;
					scroll-snap-type: x mandatory;
					scrollbar-width: none;
					-ms-overflow-style: none;
					will-change: scroll-position;
					-webkit-scroll-snap-type: x mandatory;
					scroll-behavior: smooth;
				}

				@media (max-width: 1023px) {
					.media-container {
						margin: 0 -1rem;
						padding: 0 1rem;
					}
				}

				.mobile-tab-scroll::-webkit-scrollbar {
					display: none;
				}

				.mobile-tab-item {
					flex: 0 0 auto;
					padding: 0.375rem 0.75rem;
					border-radius: 0.5rem;
					transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
					cursor: pointer;
					scroll-snap-align: center;
					white-space: nowrap;
					font-weight: 500;
					min-width: fit-content;
					touch-action: pan-x pan-y;
					-webkit-tap-highlight-color: transparent;
				}

				.mobile-tab-item.active {
					background-color: rgba(255, 255, 255, 0.15);
					color: white;
					font-weight: 600;
					transform: scale(1.05);
				}

				.mobile-tab-item.inactive {
					background-color: transparent;
					color: rgba(255, 255, 255, 0.7);
					font-weight: 500;
					transform: scale(0.9);
				}

				.mobile-tab-item:hover {
					background-color: rgba(255, 255, 255, 0.1);
					color: rgba(255, 255, 255, 0.9);
				}

				.description-container {
					display: flex;
					align-items: center;
					justify-content: center;
				}

				.description-text {
					transition: opacity 0.3s ease;
				}

				.description-text.fade-out {
					opacity: 0;
				}

				.description-text.fade-in {
					opacity: 1;
				}

				.pagination-dot {
					cursor: pointer;
					transition: all 0.3s ease;
					transform-origin: center;
				}

				.pagination-dot:hover {
					transform: scale(1.2);
				}

				.pagination-dot.active {
					background: linear-gradient(45deg, #6366f1, #8b5cf6);
					transform: scale(1.3);
					box-shadow: 0 0 12px rgba(139, 92, 246, 0.7);
				}

				.mobile-tab-container::before,
				.mobile-tab-container::after {
					content: '';
					position: absolute;
					top: 0;
					bottom: 0;
					width: 2rem;
					pointer-events: none;
					z-index: 1;
				}

				.mobile-tab-container::before {
					left: 0;
					background: linear-gradient(to right, rgba(0, 0, 0, 0.3), transparent);
				}

				.mobile-tab-container::after {
					right: 0;
					background: linear-gradient(to left, rgba(0, 0, 0, 0.3), transparent);
				}

				.mobile-nav-arrows {
					position: absolute;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					display: flex;
					align-items: center;
					justify-content: space-between;
					padding: 0 1rem;
					pointer-events: none;
					z-index: 10;
				}

				.mobile-nav-arrow {
					width: 3rem;
					height: 3rem;
					background-color: rgba(0, 0, 0, 0.5);
					border-radius: 50%;
					display: flex;
					align-items: center;
					justify-content: center;
					transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
					cursor: pointer;
					pointer-events: auto;
					backdrop-filter: blur(4px);
					border: 1px solid rgba(255, 255, 255, 0.2);
					-webkit-tap-highlight-color: transparent;
				}

				.mobile-nav-arrow:hover {
					background-color: rgba(0, 0, 0, 0.7);
					transform: scale(1.1);
					box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
				}

				.mobile-nav-arrow:active {
					transform: scale(0.95);
				}

				.mobile-nav-arrow.disabled {
					opacity: 0.3;
					cursor: not-allowed;
					pointer-events: none;
				}

				.mobile-nav-arrow svg {
					transition: transform 0.2s ease;
				}

				.mobile-nav-arrow:hover svg {
					transform: scale(1.1);
				}
			</style>

			<div class="w-full text-white py-16">
				<div class="max-w-6xl mx-auto px-4">
					<!-- Title -->
					<h2 class="text-4xl font-semibold text-center mb-8 font-sans text-white">
						${this.title}
					</h2>

					<!-- Media Container with Slider -->
					<div class="relative mb-6">
						<div class="relative rounded-2xl overflow-hidden bg-gradient-to-br">
							<div class="media-container">
								<div id="media-slider-container" style="width: 100%; height: 100%;">
									<!-- 媒体内容将在这里动态插入 -->
								</div>

								<!-- 移动端导航箭头 -->
								<div class="mobile-nav-arrows">
									<button class="mobile-nav-arrow mobile-prev-arrow ${
										this.currentTabIndex === 0 ? 'disabled' : ''
									}" ${this.currentTabIndex === 0 ? 'disabled' : ''}>
										<svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
										</svg>
									</button>
									<button class="mobile-nav-arrow mobile-next-arrow ${
										this.currentTabIndex === this.tabs.length - 1
											? 'disabled'
											: ''
									}" ${
			this.currentTabIndex === this.tabs.length - 1 ? 'disabled' : ''
		}>
										<svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
										</svg>
									</button>
								</div>
							</div>
						</div>
					</div>

					<!-- 移动端：水平滚动Tab导航 -->
					<div class="mobile-tab-container mb-6">
						<div class="mobile-tab-scroll" id="mobile-tab-scroll">
							${this.tabs
								.map(
									(tab, index) => `
									<div
										class="mobile-tab-item ${
											index === this.currentTabIndex ? 'active' : 'inactive'
										}"
										data-index="${index}"
									>
										${tab.label}
									</div>
								`
								)
								.join('')}
						</div>
					</div>

					<!-- Description Text -->
					<div class="description-container text-center mb-8">
						<p class="mt-4 description-text text-white text-xl leading-relaxed max-w-4xl mx-auto fade-in" id="description-text">
							${this.tabs[this.currentTabIndex].description}
						</p>
					</div>

					<!-- 移动端：分页导航点 -->
					<div class="pagination-dots flex justify-center space-x-3">
						${this.tabs
							.map(
								(_, index) => `
								<button class="pagination-dot w-3 h-3 rounded-full transition-all duration-300 ${
									index === this.currentTabIndex ? 'active' : 'bg-gray-600'
								}" data-index="${index}"></button>
								`
							)
							.join('')}
					</div>
				</div>
			</div>
		`;
	}

	renderDesktop() {
		this.innerHTML = `
			<style>
				.media-container {
					position: relative;
					overflow: hidden;
					aspect-ratio: 16/9;
					border-radius: 12px;
				}

				.media-slider {
					display: flex;
					width: 100%;
					height: 100%;
					transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
				}

				.media-slide-item {
					flex-shrink: 0;
				}

				.media-slide-item .media-slide {
					width: 100%;
					height: 100%;
				}

				.media-slide-item .feature-video,
				.media-slide-item .feature-image {
					width: 100%;
					height: 100%;
					object-fit: cover;
					border-radius: 12px;
				}

				.tab-navigation {
					position: relative;
				}

				.tab-underline-base {
					max-width: 700px;
					margin: 0 auto;
					height: 1px;
					background-color: rgba(255, 255, 255, 0.2);
				}

				.tab-underline-active {
					position: absolute;
					bottom: -2px;
					height: 1px;
					background-color: white;
					transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
					border-radius: 1.5px;
					opacity: 0;
				}

				.tab-underline-active.ready {
					opacity: 1;
				}

				.description-container {
					display: flex;
					align-items: start;
					justify-content: start;
				}

				.description-text {
					transition: opacity 0.3s ease;
				}

				.description-text.fade-out {
					opacity: 0;
				}

				.description-text.fade-in {
					opacity: 1;
				}

				.desktop-nav-arrows {
					position: absolute;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					display: flex;
					align-items: center;
					justify-content: space-between;
					padding: 0 2rem;
					pointer-events: none;
					z-index: 10;
				}

				.desktop-nav-arrow {
					width: 4rem;
					height: 4rem;
					background-color: rgba(0, 0, 0, 0.6);
					border-radius: 50%;
					display: flex;
					align-items: center;
					justify-content: center;
					transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
					cursor: pointer;
					pointer-events: auto;
					backdrop-filter: blur(8px);
					border: 2px solid rgba(255, 255, 255, 0.3);
					-webkit-tap-highlight-color: transparent;
				}

				.desktop-nav-arrow:hover {
					background-color: rgba(0, 0, 0, 0.8);
					transform: scale(1.1);
					box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
					border-color: rgba(255, 255, 255, 0.5);
				}

				.desktop-nav-arrow:active {
					transform: scale(0.95);
				}

				.desktop-nav-arrow.disabled {
					opacity: 0.3;
					cursor: not-allowed;
					pointer-events: none;
				}

				.desktop-nav-arrow svg {
					transition: transform 0.2s ease;
				}

				.desktop-nav-arrow:hover svg {
					transform: scale(1.1);
				}
			</style>

			<div class="w-full text-white py-16">
				<div class="md:max-w-[1200px] mx-auto px-4">
					<!-- Title -->
					<h2 class="text-6xl font-semibold text-center mb-12 font-sans text-white">
						${this.title}
					</h2>

					<!-- Media Container with Slider -->
					<div class="relative mb-8">
						<div class="relative rounded-2xl overflow-hidden bg-gradient-to-br">
							<div class="media-container">
								<div id="media-slider-container" style="width: 100%; height: 100%;">
									<!-- 媒体内容将在这里动态插入 -->
								</div>

								<!-- 桌面端导航箭头 -->
								<div class="desktop-nav-arrows">
									<button class="desktop-nav-arrow desktop-prev-arrow ${
										this.currentTabIndex === 0 ? 'disabled' : ''
									}" ${this.currentTabIndex === 0 ? 'disabled' : ''}>
										<svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
										</svg>
									</button>
									<button class="desktop-nav-arrow desktop-next-arrow ${
										this.currentTabIndex === this.tabs.length - 1
											? 'disabled'
											: ''
									}" ${
			this.currentTabIndex === this.tabs.length - 1 ? 'disabled' : ''
		}>
										<svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
										</svg>
									</button>
								</div>
							</div>
						</div>
					</div>

					<!-- Tabs Navigation -->
					<div class="tab-navigation">
						<div class="flex flex-wrap justify-center gap-4 md:gap-8 mb-2" id="tab-buttons-container">
							${this.tabs
								.map(
									(tab, index) => `
									<button
										class="tab-button relative px-4 py-2 text-2xl font-medium transition-colors duration-200 ${
											index === this.currentTabIndex
												? 'text-white'
												: 'text-gray-400 hover:text-gray-300'
										}"
										data-index="${index}"
									>
										${tab.label}
									</button>
								`
								)
								.join('')}
						</div>
						<div class="tab-underline-base"> </div>
						<div class="tab-underline-active" id="tab-underline-active"> </div>
					</div>

					<!-- Description Text -->
					<div class="description-container text-center mb-12 mt-2">
						<p class="description-text text-white text-2xl leading-relaxed max-w-4xl mx-auto fade-in mt-2" id="description-text">
							${this.tabs[this.currentTabIndex].description}
						</p>
					</div>

				</div>
			</div>
		`;
	}

	async waitForLayout() {
		return new Promise((resolve) => {
			const checkLayout = () => {
				if (this.isMobile) {
					const container = this.querySelector('.pagination-dots');
					if (container) {
						resolve();
						return;
					}
				} else {
					const container = this.querySelector('#tab-buttons-container');
					const firstButton = this.querySelector('.tab-button');

					if (container && firstButton) {
						const containerRect = container.getBoundingClientRect();
						const buttonRect = firstButton.getBoundingClientRect();

						if (containerRect.width > 0 && buttonRect.width > 0) {
							resolve();
							return;
						}
					}
				}

				requestAnimationFrame(checkLayout);
			};

			requestAnimationFrame(checkLayout);
		});
	}

	bindEvents() {
		if (this.isMobile) {
			this.bindMobileEvents();
		} else {
			this.bindDesktopEvents();
		}

		// 通用事件：滚轮和键盘导航
		this.addEventListener('wheel', this.handleWheel.bind(this), {
			passive: false
		});

		this.keyboardHandler = this.handleKeyboard.bind(this);
		document.addEventListener('keydown', this.keyboardHandler);
	}

	bindMobileEvents() {
		// 水平滚动Tab导航
		const mobileTabItems = this.querySelectorAll('.mobile-tab-item');
		mobileTabItems.forEach((item) => {
			item.addEventListener('click', (e) => {
				if (this.isTransitioning) return;
				const index = parseInt(e.currentTarget.dataset.index);
				if (!isNaN(index)) {
					this.setActiveTab(index);
				}
			});
		});

		// 分页导航点
		const paginationDots = this.querySelectorAll('.pagination-dot');
		paginationDots.forEach((dot) => {
			dot.addEventListener('click', this.handlePaginationClick.bind(this));
		});

		// 移动端导航箭头
		const mobilePrevArrow = this.querySelector('.mobile-prev-arrow');
		const mobileNextArrow = this.querySelector('.mobile-next-arrow');

		if (mobilePrevArrow) {
			mobilePrevArrow.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();
				if (!this.isTransitioning && this.currentTabIndex > 0) {
					this.navigateTab(-1);
				}
			});
		}

		if (mobileNextArrow) {
			mobileNextArrow.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();
				if (
					!this.isTransitioning &&
					this.currentTabIndex < this.tabs.length - 1
				) {
					this.navigateTab(1);
				}
			});
		}

		// 添加触摸滑动支持
		const container = this.querySelector('.media-container');
		if (container) {
			this.addTouchSupport(container);
		}
	}

	addTouchSupport(container) {
		let startX = 0;
		let startY = 0;
		let isDown = false;

		container.addEventListener(
			'touchstart',
			(e) => {
				isDown = true;
				startX = e.touches[0].clientX;
				startY = e.touches[0].clientY;
			},
			{passive: true}
		);

		container.addEventListener(
			'touchmove',
			(e) => {
				if (!isDown) return;

				const currentX = e.touches[0].clientX;
				const currentY = e.touches[0].clientY;
				const diffX = Math.abs(currentX - startX);
				const diffY = Math.abs(currentY - startY);

				if (diffX > diffY && diffX > 10) {
					e.preventDefault();
				}
			},
			{passive: false}
		);

		container.addEventListener(
			'touchend',
			(e) => {
				if (!isDown) return;
				isDown = false;

				const endX = e.changedTouches[0].clientX;
				const diffX = startX - endX;
				const threshold = 50;

				if (Math.abs(diffX) > threshold) {
					if (diffX > 0 && this.currentTabIndex < this.tabs.length - 1) {
						this.navigateTab(1);
					} else if (diffX < 0 && this.currentTabIndex > 0) {
						this.navigateTab(-1);
					}
				}
			},
			{passive: true}
		);
	}

	scrollTabToCenter(index) {
		const tabScroll = this.querySelector('#mobile-tab-scroll');
		const targetTab = this.querySelector(
			`.mobile-tab-item[data-index="${index}"]`
		);

		if (!tabScroll || !targetTab) return;

		const scrollLeft =
			targetTab.offsetLeft -
			(tabScroll.clientWidth - targetTab.offsetWidth) / 2;
		tabScroll.scrollTo({
			left: scrollLeft,
			behavior: 'smooth'
		});
	}

	bindDesktopEvents() {
		// Tab buttons
		const tabButtons = this.querySelectorAll('.tab-button');
		tabButtons.forEach((button) => {
			button.addEventListener('click', this.handleTabClick.bind(this));
		});

		// 桌面端媒体容器导航箭头
		const desktopPrevArrow = this.querySelector('.desktop-prev-arrow');
		const desktopNextArrow = this.querySelector('.desktop-next-arrow');

		if (desktopPrevArrow) {
			desktopPrevArrow.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();
				if (!this.isTransitioning && this.currentTabIndex > 0) {
					this.navigateTab(-1);
				}
			});
		}

		if (desktopNextArrow) {
			desktopNextArrow.addEventListener('click', (e) => {
				e.preventDefault();
				e.stopPropagation();
				if (
					!this.isTransitioning &&
					this.currentTabIndex < this.tabs.length - 1
				) {
					this.navigateTab(1);
				}
			});
		}

		// Resize observer
		if (window.ResizeObserver) {
			this.resizeObserver = new ResizeObserver(() => {
				this.updateActiveUnderline();
			});

			const container = this.querySelector('#tab-buttons-container');
			if (container) {
				this.resizeObserver.observe(container);
			}
		}
	}

	initializeUI() {
		if (this.isMobile) {
			this.updateMobileTabsDisplay();
			this.updatePaginationDots();
			this.updateMobileArrowStates();
			setTimeout(() => {
				this.scrollTabToCenter(this.currentTabIndex);
			}, 100);
		} else {
			this.updateActiveUnderline();
			this.updateArrowStates();
		}
	}

	handlePaginationClick(e) {
		if (this.isTransitioning) return;
		const index = parseInt(e.currentTarget.dataset.index);
		if (!isNaN(index)) {
			this.setActiveTab(index);
		}
	}

	handleTabClick(e) {
		if (this.isTransitioning) return;
		const index = parseInt(e.currentTarget.dataset.index);
		if (!isNaN(index)) {
			this.setActiveTab(index);
		}
	}

	handleWheel(e) {
		if (this.isTransitioning) return;

		const isAtStart = this.currentTabIndex === 0 && e.deltaX < 0;
		const isAtEnd =
			this.currentTabIndex === this.tabs.length - 1 && e.deltaX > 0;

		if (isAtStart || isAtEnd) {
			return;
		}

		if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
			e.preventDefault();
			this.navigateTab(e.deltaX > 0 ? 1 : -1);
		}
	}

	handleKeyboard(e) {
		if (this.isTransitioning) return;

		if (e.key === 'ArrowLeft') {
			this.navigateTab(-1);
		} else if (e.key === 'ArrowRight') {
			this.navigateTab(1);
		}
	}

	setActiveTab(index) {
		if (index === this.currentTabIndex || this.isTransitioning) return;
		if (index < 0 || index >= this.tabs.length) return;

		this.isTransitioning = true;
		this.currentTabIndex = index;

		this.updateMediaSlider();

		if (this.isMobile) {
			this.updateMobileTabsDisplay();
			this.updatePaginationDots();
			this.updateMobileArrowStates();
			this.scrollTabToCenter(index);
		} else {
			this.updateTabsDisplay();
			this.updateActiveUnderline();
			this.updateArrowStates();
		}

		this.updateDescription();

		setTimeout(() => {
			this.isTransitioning = false;
		}, 500);
	}

	navigateTab(direction) {
		const newIndex = Math.max(
			0,
			Math.min(this.tabs.length - 1, this.currentTabIndex + direction)
		);
		this.setActiveTab(newIndex);
	}

	updateMediaSlider() {
		const slider = this.querySelector('#media-slider');
		if (slider && this.mediaSlides.length > 0) {
			slider.style.transform = `translateX(-${
				(this.currentTabIndex * 100) / this.mediaSlides.length
			}%)`;
		}
	}

	updateMobileTabsDisplay() {
		const tabItems = this.querySelectorAll('.mobile-tab-item');
		tabItems.forEach((item, index) => {
			if (index === this.currentTabIndex) {
				item.className = 'mobile-tab-item active';
			} else {
				item.className = 'mobile-tab-item inactive';
			}
		});
	}

	updatePaginationDots() {
		const dots = this.querySelectorAll('.pagination-dot');
		dots.forEach((dot, index) => {
			if (index === this.currentTabIndex) {
				dot.classList.add('active');
				dot.style.background = 'linear-gradient(45deg, #6366f1, #8b5cf6)';
				dot.classList.remove('bg-gray-600');
			} else {
				dot.classList.remove('active');
				dot.style.background = '';
				dot.classList.add('bg-gray-600');
			}
		});
	}

	updateTabsDisplay() {
		const tabButtons = this.querySelectorAll('.tab-button');
		tabButtons.forEach((button, index) => {
			const isActive = index === this.currentTabIndex;
			button.className = `tab-button relative px-4 py-2 text-2xl font-medium transition-colors duration-200 ${
				isActive ? 'text-white' : 'text-gray-400 hover:text-gray-300'
			}`;
		});
	}

	updateActiveUnderline() {
		const activeButton = this.querySelector(
			`.tab-button[data-index="${this.currentTabIndex}"]`
		);
		const underline = this.querySelector('#tab-underline-active');
		const container = this.querySelector('#tab-buttons-container');

		if (!activeButton || !underline || !container) {
			return;
		}

		const containerRect = container.getBoundingClientRect();
		const buttonRect = activeButton.getBoundingClientRect();

		if (containerRect.width === 0 || buttonRect.width === 0) {
			return;
		}

		const left = buttonRect.left - containerRect.left;
		const width = buttonRect.width;

		underline.style.left = `${left}px`;
		underline.style.width = `${width}px`;

		requestAnimationFrame(() => {
			underline.classList.add('ready');
		});
	}

	updateDescription() {
		const descriptionText = this.querySelector('#description-text');
		if (!descriptionText) return;

		descriptionText.classList.remove('fade-in');
		descriptionText.classList.add('fade-out');

		setTimeout(() => {
			descriptionText.textContent = this.tabs[this.currentTabIndex].description;
			descriptionText.classList.remove('fade-out');
			descriptionText.classList.add('fade-in');
		}, 150);
	}

	updateArrowStates() {
		const desktopPrevArrow = this.querySelector('.desktop-prev-arrow');
		const desktopNextArrow = this.querySelector('.desktop-next-arrow');

		if (desktopPrevArrow) {
			if (this.currentTabIndex === 0) {
				desktopPrevArrow.disabled = true;
				desktopPrevArrow.classList.add('disabled');
			} else {
				desktopPrevArrow.disabled = false;
				desktopPrevArrow.classList.remove('disabled');
			}
		}

		if (desktopNextArrow) {
			if (this.currentTabIndex === this.tabs.length - 1) {
				desktopNextArrow.disabled = true;
				desktopNextArrow.classList.add('disabled');
			} else {
				desktopNextArrow.disabled = false;
				desktopNextArrow.classList.remove('disabled');
			}
		}
	}

	updateMobileArrowStates() {
		const mobilePrevArrow = this.querySelector('.mobile-prev-arrow');
		const mobileNextArrow = this.querySelector('.mobile-next-arrow');

		if (mobilePrevArrow) {
			if (this.currentTabIndex === 0) {
				mobilePrevArrow.disabled = true;
				mobilePrevArrow.classList.add('disabled');
			} else {
				mobilePrevArrow.disabled = false;
				mobilePrevArrow.classList.remove('disabled');
			}
		}

		if (mobileNextArrow) {
			if (this.currentTabIndex === this.tabs.length - 1) {
				mobileNextArrow.disabled = true;
				mobileNextArrow.classList.add('disabled');
			} else {
				mobileNextArrow.disabled = false;
				mobileNextArrow.classList.remove('disabled');
			}
		}
	}

	cleanup() {
		if (this.keyboardHandler) {
			document.removeEventListener('keydown', this.keyboardHandler);
			this.keyboardHandler = null;
		}

		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
		}
	}
}

// Register the custom element
customElements.define('feature-showcase', FeatureShowcase);
